import {
  useState, useEffect, useRef,
} from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { Flex, Box } from 'reflexbox';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Loader, RadioButton, Button, ModalWindow, Card,
} from 'components';
import { formatHealthIcon, labelDescHealth } from 'components/HealthIcon';
import { TextArea } from '~/components/NewInputs/TextArea';
import { getCachedDevInfo, getCachedDevInfoSync } from 'helpers/cachedStorage';
import { getUserProfile } from 'helpers/userProfile';
import { useStateVar } from 'helpers/useStateVar';
import {
  AnnotationIcon,
  InfoCircleIcon,
  SearchNewIcon,
} from 'icons';
import { approveOrRejectSelectedPendingFaults } from '~/pages/Admin/FaultsList';
import { ApiResps, apiCall, apiCallFormDataMultFiles } from 'providers';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

import {
  SelectedStatusText,
  StatusText,
  StyledBox,
  Icon,
  CardWrapper,
  CardTitle,
  GreenButton,
  YellowButton,
  OrangeButton,
  RedButton,
  SaveButton,
  BtnExport,
  ExportWorksheet,
  Text,
  ButtonCustom,
  ContainerHealths,
  MaintenanceValidationHealthIconBox,
  MaintenanceValidationStatusLabel,
  MaintenanceValidationNote,
  MaintenanceValidationNoteTitle,
  MaintenanceValidationNoteBody,
} from './styles';
import styled from 'styled-components';
import { generateNameFormatted } from '~/helpers/titleHelper';
import moment, { Moment } from 'moment';
import { formatPhone } from '~/helpers/formatPhone';
import { splitAttachments } from '~/helpers/file';
import { HealthHistoryTable } from '~/components/HealthsTable/healthHistoryTable';
import { ObservationTable } from '~/components/HealthsTable/observationTable';
import { FeedbackModal } from './FeedbackModal';
import { MaintenanceValidationCard, shouldShowMaintenanceValidation } from './MaintenanceValidationCard';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export interface ObservationInfo {
  devId: string;
  date: string;
  OBS_DESC: string;
  userId: string;
  observationId: string;
}

type ImagePreview = {
  file: Blob;
  preview: string;
};

type PdfPreview = {
  file: Blob;
  preview: string;
  name: string;
  contentType?: string;
};

export const Health = (): JSX.Element => {
  const query = useQuery();
  const location = useLocation();
  const { devId } = useParams<{ devId: string }>();
  const [profile] = useState(() => getUserProfile());
  const [descObs, setDescObs] = useState<string>();
  const csvLinkEl = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [machineUnderAnalysis, setMachineUnderAnalysis] = useState(false);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [pdfs, setPdfs] = useState<PdfPreview[]>([]);
  const [state, render, setState] = useStateVar({
    selectedDate: null as Moment | null,
    isFocusedDate: false,
    tomorrow: moment(moment().add(1, 'days').format('YYYY-MM-DD')),
    devInfo: getCachedDevInfoSync(devId),
    feedbackInfo: {} as {
      ID: number;
      ASSET_HEALTH_HIST_ID: number;
      REASON: string;
      USER_NAME: string;
      PHONE_NUMBER?: string;
      MAINTENANCE_DONE?: number;
      MAINTENANCE_DATE?: string;
      ASSET_ID: number;
      IMAGES?: string[];
    },
    healthIndexes: {},
    currentHealthIndex: null as null|number,
    currentHealthTitle: null as null|string,
    currentLaudo: null as null|string,
    currentPosCauses: [] as string[],
    selectedLevelTitle: null as null|string,
    isLoading: true,
    isLoadingModal: false,
    isSaving: false,
    newHealthIndex: null as null|number,
    showAddObservation: false as boolean,
    newFaultId: null as null|string,
    assignableFaults: null as null|ApiResps['/asset/get-assignable-faults'],
    possibleCauses: {} as { [id: string]: { text: string } },
    detectedFaults: [] as { origin: string; faultId: string; faultDesc: string; severity: string; faultLevel: number; actionAprove: boolean; actionTaken?: string; origem: string; lastAction:string }[],
    healthHist: [] as { devId: string; assetId: number; date: string; DAT_REPORT: number; healthIndex: string; H_INDEX: number; possCauses: string[]; UNIT_ID: number; desc: string; changeType: string; }[],
    observations: [] as { devId: string; date: string; OBS_DESC: string; userId: string; observationId: string;}[],
    modalEditObservation: null as null|{
      addEdit: 'Add'|'Edit'
      observationId: string
    },
    currIdHealth: null as null|number,
    csvData: [] as {}[],
    assetLayout: false as boolean,
    reason: null as null|string,
    phone: null as null|string,
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    Array.from(e.target.files).forEach((file) => {
      const preview = URL.createObjectURL(file);
      if (file.type === 'application/pdf') {
        setPdfs((prev) => prev.concat({
          file,
          preview,
          name: file.name,
          contentType: file.type,
        }));
      } else {
        setImages((prev) => prev.concat({ file, preview }));
      }
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      const newImages = [...prev];
      const [removed] = newImages.splice(index, 1);
      URL.revokeObjectURL(removed.preview);
      return newImages;
    });
  };

  const removePdf = (index: number) => {
    setPdfs((prev) => {
      const newPdfs = [...prev];
      const [removed] = newPdfs.splice(index, 1);
      URL.revokeObjectURL(removed.preview);
      return newPdfs;
    });
  };

  const CSVheader = [
    { label: t('dispositivo'), key: 'id' },
    { label: t('status'), key: 'status' },
    { label: t('descricao'), key: 'descricao' },
    { label: t('data'), key: 'data' },
    { label: t('causa'), key: 'causa' },
    { label: t('tipoMudanca'), key: 'tipo' },
  ];

  const handleModalClose = () => {
    if (state.feedbackInfo) {
      const params = new URLSearchParams(location.search);
      params.delete('modal');
      history.replace({ search: params.toString() });
      return setIsModalOpen(false);
    }
    setIsModalOpen(false);
    setImages([]);
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setPdfs([]);
    pdfs.forEach((pdf) => URL.revokeObjectURL(pdf.preview));
    setMaintenance(false);
    setState({
      selectedDate: null,
      reason: null,
      phone: null,
    });
    render();

    const params = new URLSearchParams(location.search);
    params.delete('modal');
    history.replace({ search: params.toString() });
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    render();

    const params = new URLSearchParams(location.search);
    params.set('modal', 'true');
    history.replace({ search: params.toString() });
  };

  useEffect(() => {
    if (query.get('modal') === 'true') {
      setIsModalOpen(true);
      render();
    }
  }, [location.search]);

  async function submitFeedback() {
    try {
      if (state.currIdHealth && state.reason) {
        setState({ isLoadingModal: true });
        await apiCallFormDataMultFiles('/asset/save-maintainer-feedback', {
          asset_health_hist_id: state.currIdHealth,
          reason: state.reason,
          phone_number: state.phone || undefined,
          maintenance_done: maintenance === true ? 1 : 0,
          maintenance_date: state.selectedDate
            ? state.selectedDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          client_id: state.devInfo.CLIENT_ID,
          unit_id: state.devInfo.UNIT_ID,
        }, {
          files: (() => {
            const all = [...images.map((img) => img.file), ...pdfs.map((pdf) => pdf.file)];
            return all.length > 0 ? all : null;
          })(),
        });
        handleModalClose();
        setState({ isLoadingModal: false });
        toast.success(t('sucessoEnviarFeedbackSaude'));
        await handleGetDevHealth();
      } else {
        toast.error(t('erroEnviarFeedbackSaude'));
      }
    } catch (err) {
      console.log(err);
      setState({ isLoadingModal: false });
      toast.error(t('erroEnviarFeedbackSaude'));
    }
  }

  const history = useHistory();
  const linkBase = history.location.pathname;
  state.assetLayout = linkBase.includes('/ativo');

  function buildDetectedFault(
    faultData: {
      origin: string,
      id: string,
      faultName: string,
      faultLevel: number,
      lastAction: string,
      lastActionTime: number
    },
    healthIndexes: { [k: string]: { titulo: string }},
  ) {
    const fdata = {
      origin: faultData.origin,
      faultId: faultData.id,
      faultDesc: faultData.faultName,
      severity: (healthIndexes[String(faultData.faultLevel)] || {}).titulo || '?',
      faultLevel: faultData.faultLevel,
      actionAprove: true,
      actionTaken: undefined as string|undefined,
      origem: faultData.origin,
      lastAction: faultData.lastAction,
    };
    if (faultData.lastActionTime) {
      const actionTime = new Date(faultData.lastActionTime);
      const approvableActions: string[] = ['PENDING', 'RESTAB_PENDING'];
      if (!approvableActions.includes(faultData.lastAction)) {
        fdata.actionTaken = `${faultData.lastAction} ${actionTime.toLocaleString()}`;
        fdata.actionAprove = false;
      }
    }
    return fdata;
  }

  async function verifyHealthStatus(healthStatus: ApiResps['/asset/get-health-status']['healthStatus'], healthIndexes: ApiResps['/faults/get-fault-codes']['healthIndexes']) {
    if (healthStatus) {
      state.detectedFaults = [];
      if (healthStatus.fdetected) {
        healthStatus.fdetected.forEach((faultData) => {
          const fdata = buildDetectedFault(faultData, healthIndexes);
          state.detectedFaults.push(fdata);
        });
      }
    }
  }

  async function handleGetDevHealth() {
    try {
      setState({ isLoading: true });

      const devInfo = await getCachedDevInfo(devId);
      state.devInfo = devInfo;
      getObservations();

      if (devInfo?.ASSET_ID) {
        const [{ possibleCauses, healthIndexes },
          { list: healthList },
          { healthStatus }] = await Promise.all([
          apiCall('/faults/get-fault-codes', {}),
          apiCall('/asset/get-health-hist', { assetId: devInfo.ASSET_ID }),
          apiCall('/asset/get-health-status', { ASSET_ID: devInfo.ASSET_ID }),
          profile.permissions.HEALTH_MANAGEMENT ? loadAssignableFaults(devInfo.ASSET_ID) : null,
        ]);

        state.healthIndexes = (healthIndexes || {});
        state.possibleCauses = (possibleCauses || {});
        state.healthHist = (healthList || []);
        state.currIdHealth = healthStatus.ASSET_HEALTH_HIST_ID;
        state.currentHealthIndex = healthStatus.H_INDEX;
        state.currentHealthTitle = ((state.healthIndexes || {})[String(healthStatus.H_INDEX)] || {}).titulo;
        state.currentLaudo = healthStatus.H_DESC;
        state.currentPosCauses = (healthStatus.P_CAUSES || '')
          .split(',')
          .map((id) => (possibleCauses?.[id]?.text))
          .filter((x) => !!x);
        verifyHealthStatus(healthStatus, healthIndexes);

        const feedback = await apiCall('/asset/get-maintainer-feedback-by-user', {
          asset_health_hist_id: healthStatus.ASSET_HEALTH_HIST_ID,
          client_id: devInfo.CLIENT_ID,
          unit_id: devInfo.UNIT_ID,
        });

        setState({ feedbackInfo: feedback });
        if (feedback) {
          setMaintenance(feedback.MAINTENANCE_DONE === 1);
          setState({
            phone: feedback.PHONE_NUMBER ?? null,
            reason: feedback.REASON,
            selectedDate: feedback.MAINTENANCE_DATE ? moment(feedback.MAINTENANCE_DATE) : null,
          });

          const { images: imageUrls, documents } = await splitAttachments(feedback);
          setImages(imageUrls.map((url) => ({ file: new Blob(), preview: url })));
          setPdfs(documents.map(({ url, name, contentType }) => ({
            file: new Blob(),
            preview: url,
            name,
            contentType,
          })));
        }
      }
      render();
    } catch (err) {
      console.log(err);
      toast.error(t('erroSaudeMaquinas'));
    }
    setState({ isLoading: false });
  }

  async function loadAssignableFaults(assetId: number) {
    try {
      state.assignableFaults = await apiCall('/asset/get-assignable-faults', { assetId });
    } catch (err) {
      console.log(err);
      state.assignableFaults = null;
      toast.error(t('erroCarregarFalhasAtribuiveis'));
    }
  }

  async function saveHealthInfo() {
    if (state.isSaving) return;
    try {
      if (!state.devInfo.ASSET_ID) {
        toast.warn(t('erroSalvarSaudeSemAtivoRelacionado'));
        return;
      }
      if (state.newHealthIndex == null) {
        toast.error(t('erroIndiceSaude'));
        return;
      }
      if ((state.newHealthIndex !== 100) && !state.newFaultId) {
        toast.error(t('selecioneFalha'));
        return;
      }
      setState({ isSaving: true });
      await apiCall('/asset/save-health-info', {
        assetId: state.devInfo.ASSET_ID,
        healthIndex: state.newHealthIndex,
        faultId: state.newHealthIndex === 100 ? null : state.newFaultId,
      });
      toast.success(t('sucessoSalvar'));
      state.newHealthIndex = null;
      state.newFaultId = null;
      render();
      await handleGetDevHealth();
    } catch (err) {
      console.log(err);
      toast.error(t('erro'));
    }
    setState({ isSaving: false });
  }

  const getCsvData = async () => {
    state.isLoading = true; render();
    const formattedCSV = [] as any;
    try {
      if (state.healthHist) {
        state.healthHist.forEach((item) => {
          formattedCSV.push({
            id: item.devId,
            status: labelDescHealth(item.H_INDEX),
            descricao: item.desc,
            data: new Date(item.date).toLocaleString('pt-BR'),
            causa: (item.possCauses && (item.possCauses.length > 0)) && (
              item.possCauses.map((cause) => `• ${cause}\n`).join('')
            ),
            tipo: item.changeType,
          });
        });

        state.csvData = formattedCSV;
        render();
        setTimeout(() => {
          csvLinkEl.current?.link.click();
        }, 1000);

        state.isLoading = false; render();
      }
      else {
        toast.info(t('naoHaDadosSaudeParaExportar')); state.isLoading = false;
      }
    } catch (err) { console.log(err); toast.error(t('houveErro')); state.isLoading = false; }
  };

  async function wantDeleteHistoryEvent(item: {
    devId: string;
    assetId: number;
    date: string;
    DAT_REPORT: number;
    desc: string;
    changeType: string;
    healthIndex: string;
    possCauses: string[];
    UNIT_ID: number;
    H_INDEX: number;
  }) {
    if (window.confirm(`${t('desejaExcluir')} "${item.desc}" [${item.changeType}]?`)) {
      await apiCall('/asset/delete-health-hist', {
        assetId: item.assetId,
        itemDate: item.DAT_REPORT,
        healthIndex: item.H_INDEX,
      });
      window.location.reload();
    }
  }

  function isMaintainerOnDevice(): boolean {
    const clientId = state.devInfo?.CLIENT_ID;
    const unitId = state.devInfo?.UNIT_ID;
    if (!clientId) return false;
    const onClient = profile.permissions.PER_CLIENT.some((x) => x.clientId === clientId && x.p.includes('[MN]'));
    const onUnit = (profile.permissions.PER_UNIT || []).some((x) => x.clientId === clientId && !!unitId && x.units.includes(unitId) && x.p.includes('[MN]'));
    return onClient || onUnit;
  }

  function hasObservationsAccess(): boolean {
    return profile.manageAllClients || !!profile.permissions.isInstaller || isMaintainerOnDevice();
  }

  async function getObservations() {
    if (hasObservationsAccess() && state.devInfo?.ASSET_ID) {
      const { list: observationsList } = await apiCall('/asset/get-observation', { assetId: state.devInfo.ASSET_ID });
      setState({
        observations: (observationsList || []),
      });
    }
  }

  useEffect(() => {
    handleGetDevHealth();
  }, []);

  function onLevelClick(newHealthIndex: number) {
    if (newHealthIndex !== 100) {
      if (!state.assignableFaults) {
        toast.error(t('erroCarregarFalhasAtribuiveis'));
        return;
      }
      if (!state.assignableFaults.monitored) {
        toast.warn(t('dispositivoNaoMonitoradoSaude'));
        return;
      }
    }
    const selectedLevelTitle = ((state.healthIndexes || {})[String(newHealthIndex)] || {}).titulo;
    setState({ newHealthIndex, selectedLevelTitle, newFaultId: null });
  }

  const selectedColorFaults = (state.assignableFaults?.faults ?? []).filter((fault) => fault.healthIndex === state.newHealthIndex);
  const noFaultForSelectedColor = state.newHealthIndex !== 100 && selectedColorFaults.length === 0;
  const saveDisabled = noFaultForSelectedColor || state.isSaving || state.isLoading;

  // mostra o laudo que será gravado para a falha, com as causas prováveis; sem laudo de mesmo nome, mostra o nome da falha
  function faultLabel(fault: typeof selectedColorFaults[number]) {
    if (!fault.laudo) return fault.name;
    const causesDesc = fault.laudo.pcauses.map((id) => state.possibleCauses[id]?.text).filter((x) => !!x);
    return causesDesc.length ? `${fault.laudo.text} (${causesDesc.join(', ')})` : fault.laudo.text;
  }

  async function wantDeleteObservation(item: ObservationInfo) {
    if (hasObservationsAccess())
    {
      try {
        if (window.confirm(`${t('desejaExcluir')} "${item.OBS_DESC}"?`))
        {
          const itemDate = Math.round(new Date(item.date).getTime() / 1000);
          await apiCall('/asset/delete-observation', {
            assetId: state.devInfo.ASSET_ID,
            itemDate,
          });
          toast.success(t('observacaoExcluida'));
          await getObservations();
        }
      } catch (err) { console.log(err); toast.error(t('houveErro')); }
    }
  }

  function wantEditAddProgramming(item?: ObservationInfo) {
    try {
      state.modalEditObservation = {
        addEdit: item ? 'Edit' : 'Add',
        observationId: (item && item.observationId) || '',
      };
      setDescObs((item && item.OBS_DESC) || '');
      render();
    } catch (err) { console.log(err); toast.error(t('houveErro')); }
  }

  async function saveNewObservation() {
    if (!state.devInfo.ASSET_ID) {
      toast.warn(t('erroAdicionarObservacaoSemAtivoRelacionado'));
    } else if (hasObservationsAccess())
    {
      try {
        await apiCall('/asset/save-observation-info', {
          assetId: state.devInfo.ASSET_ID,
          observationDesc: descObs,
        });
        state.modalEditObservation = null;
        toast.success(t('sucessoSalvar'));
        await getObservations();
      } catch (err) {
        console.log(err);
        toast.error('Erro');
      }
    }
  }

  async function editObservation() {
    if (hasObservationsAccess())
    {
      try {
        await apiCall('/asset/edit-observation-info', {
          observationId: state.modalEditObservation!.observationId,
          observationDesc: descObs,
        });
        state.modalEditObservation = null;
        toast.success(t('sucessoSalvar'));
        await getObservations();
      } catch (err) {
        console.log(err);
        toast.error('Erro');
      }
    }
  }

  function showAddObservation() {
    try {
      state.showAddObservation = true;
      render();
    } catch (err) { console.log(err); toast.error(t('houveErro')); }
  }

  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted(state.devInfo?.DEV_ID, t('saude'))}</title>
      </Helmet>
      {(state.isLoading)
        && (
        <Flex alignItems="center" justifyContent="center" mt="32px">
          <Box width={1} alignItems="center" justifyContent="center">
            <Loader />
          </Box>
        </Flex>
        )}
      {(!state.isLoading)
        && (
        <Flex flexDirection="column">
          <Box mt="32px" width="100%">
            <CardWrapper style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column' }}>
              <ContainerHealths>
                <Flex>
                  <StyledBox health={25} selected={!machineUnderAnalysis && state.currentHealthIndex === 25}>
                    <Icon>
                      {formatHealthIcon(25)}
                    </Icon>
                    {machineUnderAnalysis ? (
                      <MaintenanceValidationStatusLabel>{t('maquinaEmValidacao')}</MaintenanceValidationStatusLabel>
                    ) : (
                      <SelectedStatusText health={state.currentHealthIndex || 0}>{state.currentHealthTitle}</SelectedStatusText>
                    )}
                  </StyledBox>
                  <StyledBox health={50} selected={!machineUnderAnalysis && state.currentHealthIndex === 50}>
                    <Icon>
                      {formatHealthIcon(50)}
                    </Icon>
                  </StyledBox>
                  <StyledBox health={75} selected={!machineUnderAnalysis && state.currentHealthIndex === 75}>
                    <Icon>
                      {formatHealthIcon(75)}
                    </Icon>
                  </StyledBox>
                  <StyledBox health={100} selected={!machineUnderAnalysis && state.currentHealthIndex === 100}>
                    <Icon>
                      {formatHealthIcon(100)}
                    </Icon>
                  </StyledBox>
                  {machineUnderAnalysis && (
                    <MaintenanceValidationHealthIconBox>
                      <SearchNewIcon color="white" size={28} />
                    </MaintenanceValidationHealthIconBox>
                  )}
                  <StyledBox health={0} selected={!machineUnderAnalysis && ![4, 25, 50, 75, 100].includes(state.currentHealthIndex!)}>
                    <Icon>
                      {formatHealthIcon(0)}
                    </Icon>
                  </StyledBox>
                  <StyledBox health={4} selected={!machineUnderAnalysis && state.currentHealthIndex === 4}>
                    <Icon>
                      {formatHealthIcon(4)}
                    </Icon>
                  </StyledBox>
                </Flex>
                <Flex mt="16px" style={{ color: colors.Grey400 }}>
                  <Box>
                    {machineUnderAnalysis ? (
                      <>
                        <StatusText isBold>{t('laudoTecnico')}</StatusText>
                        <StatusText>{t('laudoValidacaoManutencao')}</StatusText>
                        <MaintenanceValidationNote>
                          <span style={{ flexShrink: 0, marginTop: 2, display: 'flex' }}>
                            <InfoCircleIcon color="#3180C6" size={16} />
                          </span>
                          <div>
                            <MaintenanceValidationNoteTitle>{t('diagnosticoEmAndamento')}</MaintenanceValidationNoteTitle>
                            <MaintenanceValidationNoteBody>{t('diagnosticoEmAndamentoDescricao')}</MaintenanceValidationNoteBody>
                          </div>
                        </MaintenanceValidationNote>
                      </>
                    ) : (
                      <>
                        {(state.currentLaudo)
                          && (
                          <>
                            <StatusText isBold>{t('laudoTecnico')}</StatusText>
                            <StatusText>{state.currentLaudo}</StatusText>
                          </>
                          )}
                        {((state.currentPosCauses || []).length === 1)
                          && (
                          <div>
                            {`${t('possiveisCausas')}: `}
                            {state.currentPosCauses[0]}
                          </div>
                          )}
                        {((state.currentPosCauses || []).length > 1)
                          && (
                          <div>
                            {`${t('possiveisCausas')}: `}
                            {state.currentPosCauses.map((cause) => (
                              <div>
                                -
                                {' '}
                                {cause}
                              </div>
                            ))}
                          </div>
                          )}
                      </>
                    )}
                  </Box>
                </Flex>
              </ContainerHealths>
              {state.currentHealthTitle && (
                <Flex style={{ flexDirection: 'column' }}>
                  <AnnotationIcon size={24} />
                  <Flex mt={2} mb={3}>
                    {state.feedbackInfo ? t('esteLaudoJaFoiComplementado') : t('gostariaDeComplementarEsseLaudo?')}
                  </Flex>
                  <ButtonCustom
                    width="170px"
                    height="40px"
                    onClick={handleModalOpen}
                  >
                    {state.feedbackInfo ? t('verInformacoes') : t('adicionarInformacoes')}
                  </ButtonCustom>
                </Flex>
              )}
            </CardWrapper>
          </Box>
          {profile.permissions.HEALTH_MANAGEMENT && (
          <CardWrapper style={{ marginTop: '32px' }} data-testid="health-edit-card">
            <CardTitle>{t('editarSaude')}</CardTitle>
            <div>
              <div style={{ paddingBottom: '20px', paddingTop: '20px' }}>
                <GreenButton selected={state.newHealthIndex === 100} onClick={() => onLevelClick(100)}>{t('corVerde')}</GreenButton>
                <YellowButton selected={state.newHealthIndex === 75} onClick={() => onLevelClick(75)}>{t('corAmarelo')}</YellowButton>
                <OrangeButton selected={state.newHealthIndex === 50} onClick={() => onLevelClick(50)}>{t('corLaranja')}</OrangeButton>
                <RedButton selected={state.newHealthIndex === 25} onClick={() => onLevelClick(25)}>{t('corVermelho')}</RedButton>
              </div>
              {(state.newHealthIndex != null)
                  && (
                  <div>
                    <div style={{ paddingBottom: '20px', color: colors.Grey400 }} data-testid="health-edit-level">
                      {t('nivel')}
                      {' '}
                      {state.selectedLevelTitle || '?'}
                    </div>
                    {noFaultForSelectedColor && (
                      <div style={{ color: colors.Grey400 }} data-testid="health-edit-no-fault">{t('nenhumaFalhaDestaCor')}</div>
                    )}
                    {state.newHealthIndex !== 100 && selectedColorFaults.map((fault) => (
                      <div key={fault.faultId} data-testid="health-edit-fault-option" data-fault-id={fault.faultId}>
                        <RadioButton
                          label={faultLabel(fault)}
                          checked={state.newFaultId === fault.faultId}
                          onClick={() => setState({ newFaultId: fault.faultId })}
                          style={{ marginTop: '5px' }}
                        />
                      </div>
                    ))}
                    <div>
                      <SaveButton
                        variant={saveDisabled ? 'disabled' : 'primary'}
                        style={{ marginTop: '15px' }}
                        onClick={() => !saveDisabled && saveHealthInfo()}
                      >
                        {t('salvar')}
                      </SaveButton>
                    </div>
                  </div>
                  )}
            </div>
          </CardWrapper>
          )}
          {shouldShowMaintenanceValidation(devId, state.devInfo?.MACHINE_TYPE) && (
            <Box mt="32px" width="100%">
              <MaintenanceValidationCard
                devId={devId}
                assetId={state.devInfo?.ASSET_ID}
                machineId={state.devInfo?.GROUP_ID}
                machineType={state.devInfo?.MACHINE_TYPE}
                timezoneArea={state.devInfo?.TIMEZONE_AREA}
                latestHealthDatReport={state.healthHist[0]?.DAT_REPORT}
                onMachineUnderAnalysis={setMachineUnderAnalysis}
              />
            </Box>
          )}

          {(state.healthHist
            && (
            <CardWrapper style={{ marginTop: '32px' }} data-testid="health-history-card">
              <Flex alignItems="center" justifyContent="space-between" mt="32px">
                <CardTitle>{t('historicoSaude')}</CardTitle>
                <Flex alignItems="center" justifyContent="right" mt="32">
                  <BtnExport onClick={getCsvData}>
                    <div>
                      <ExportWorksheet />
                      <Text style={{ paddingLeft: '5px' }}>
                        {t('exportarPlanilha')}
                      </Text>
                    </div>
                  </BtnExport>
                  <CSVLink
                    headers={CSVheader}
                    data={state.csvData}
                    filename={t('historicoDeSaudeDoDispositivosCsv')}
                    separator=";"
                    asyncOnClick
                    enclosingCharacter="'"
                    ref={csvLinkEl}
                  />
                </Flex>
              </Flex>
              <HealthHistoryTable
                data={state.healthHist}
                profile={profile}
                t={t}
                onDelete={wantDeleteHistoryEvent}
              />
            </CardWrapper>
            )
          )}

          {(hasObservationsAccess() && state.observations) && (
          <CardWrapper style={{ marginTop: '32px' }}>
            <Flex alignItems="center" justifyContent="space-between" mt="32px">
              <CardTitle>{t('observacoes')}</CardTitle>
              <Flex alignItems="center" justifyContent="right" mt="32">
                <Button
                  variant="primary"
                  style={{ width: 'fit-content', padding: '6px 15px' }}
                  onClick={() =>
                  { showAddObservation();
                    wantEditAddProgramming(); }}
                >
                  {t('adicionarObservacao')}
                </Button>
              </Flex>
            </Flex>
            <ObservationTable
              data={state.observations}
              t={t}
              onEdit={wantEditAddProgramming}
              onDelete={wantDeleteObservation}
            />
          </CardWrapper>
          )}

          {state.modalEditObservation && (
          <div style={{ zIndex: 3, position: 'sticky' }}>
            <ModalWindow
              style={{
                padding: '0px',
                marginBottom: 'auto',
                marginTop: '8%',
                minWidth: '500px',
                zIndex: 5,
              }}
              onClickOutside={() => {
                setState({ modalEditObservation: null });
              }}
            >
              <Card>
                <Flex flexDirection="column">
                  <span style={{
                    fontWeight: 700,
                    fontSize: '15px',
                    lineHeight: ' 18px',
                    marginBottom: '18px',
                    color: ' #000',
                  }}
                  >
                    {`${state.modalEditObservation.addEdit === 'Edit' ? t('botaoEditar') : t('adicionar')} ${t('observacao')}`}
                  </span>

                  <span style={{
                    fontWeight: 700,
                    fontSize: '12px',
                    lineHeight: ' 15px',
                    marginBottom: '9px',
                    color: ' #000',
                  }}
                  >
                    {t('descricao')}
                  </span>

                  <TextArea
                    value={descObs}
                    onChange={setDescObs}
                    error={undefined}
                    style={{
                      width: '100%',
                      marginBottom: '26px',
                      padding: '5px',
                    }}
                    maxLength={250}
                  />

                  <Button
                    variant="primary"
                    style={{
                      width: 'fit-content', padding: '6px 15px', marginInline: 'auto', marginBottom: '22px',
                    }}
                    onClick={() => {
                      state.modalEditObservation?.addEdit === 'Edit' ? editObservation() : saveNewObservation();
                    }}
                  >
                    {state.modalEditObservation?.addEdit === 'Edit' ? t('salvar') : t('adicionar')}
                  </Button>
                  <CancelButton
                    onClick={() => {
                      setState({ modalEditObservation: null });
                    }}
                  >
                    {t('cancelar')}
                  </CancelButton>
                </Flex>

              </Card>
            </ModalWindow>
          </div>
          )}

          {isModalOpen && (
            <FeedbackModal
              isLoading={state.isLoadingModal}
              state={state}
              setState={setState}
              maintenance={maintenance}
              setMaintenance={setMaintenance}
              images={images}
              pdfs={pdfs}
              handleModalClose={handleModalClose}
              handleImageChange={handleImageChange}
              removeImage={removeImage}
              removePdf={removePdf}
              formatPhone={formatPhone}
              submitFeedback={submitFeedback}
              render={render}
            />
          )}
        </Flex>
        )}
    </>
  );
};

const CancelButton = styled.a`
  display: flex;
  justify-content: center;
  font-size: 13px;
  line-height: 16px;
  color: ${colors.brand.primary.dark};
  margin-inline: auto;
  text-decoration-line: underline;
`;
